import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import treasureChestAnimation from '../effects/treasure-chest.json';
import { ProfileEffectOverlay } from '../effects';
import Particles from '../effects/Particles';

const PIXIE_DUST_EFFECT = {
  name: "Pixie Dust",
  slug: "pixie-dust",
  type: "profile-effect",
  width: 450,
  height: 880,
  effects: [
    {
      src: "/uploads/profile-effects/pixie-dust/layer-0.png",
      loop: true,
      start: 0,
      duration: 2880,
      zIndex: 100,
      position: { x: 0, y: 0 },
      loopDelay: 2880,
      width: 450,
      height: 880
    }
  ],
  reducedMotionSrc: "/uploads/profile-effects/pixie-dust/static.png"
};

export default function LottieTreasureChest({
  coinsAwarded,
  oldBalance,
  onComplete,
  variant = 'viikkoraha',
  isDark = false,
}) {
  const [phase, setPhase] = useState('card'); // card | shaking | opening | counting | idle
  const [displayCoins, setDisplayCoins] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [showCoinExplosion, setShowCoinExplosion] = useState(false);
  const lottieRef = useRef(null);
  const countingStarted = useRef(false);
  const explosionTriggered = useRef(false);

  const isBonus = variant === 'viikonloppubonus';
  const accentColor = isBonus ? '#2196f3' : '#ffc107';
  const textColor = isBonus ? '#2196f3' : '#f59e0b';

  const animateCount = useCallback((from, to, duration) => {
    const start = performance.now();
    const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOutQuart(progress);
      setDisplayCoins(Math.round(from + (to - from) * eased));

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        setTimeout(() => {
          setShowResult(true);
          setPhase('idle');
        }, 300);
      }
    };
    requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    // Small delay to ensure Lottie component has mounted
    const timer = setTimeout(() => {
      if (!lottieRef.current) return;

      if (phase === 'shaking') {
        lottieRef.current.playSegments([0, 56], true);
      } else if (phase === 'opening') {
        lottieRef.current.playSegments([56, 103], true);
      } else if (phase === 'idle') {
        // Smooth loop segment (frames 103-256)
        lottieRef.current.playSegments([103, 256], true);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [phase]);

  const handleCardTap = () => {
    if (phase !== 'card') return;
    setPhase('shaking');

    setTimeout(() => {
      setPhase('opening');
      // Only trigger explosion once
      if (!explosionTriggered.current) {
        explosionTriggered.current = true;
        setShowCoinExplosion(true);
      }

      setTimeout(() => {
        if (!countingStarted.current) {
          countingStarted.current = true;
          setPhase('counting');
          animateCount(0, coinsAwarded, 4000);
        }
      }, 600);
    }, 2000);
  };

  const handleDismiss = () => {
    if (phase === 'idle') {
      onComplete?.();
    }
  };

  const showDarkBg = phase !== 'card';
  const showEffect = phase === 'opening' || phase === 'counting' || phase === 'idle';
  const showCounter = phase === 'counting' || phase === 'idle';
  const showAnimatedChest = phase !== 'card';

  return (
    <Box
      onClick={phase === 'card' ? handleCardTap : (phase === 'idle' ? handleDismiss : undefined)}
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 100,
        cursor: (phase === 'card' || phase === 'idle') ? 'pointer' : 'default',
        borderRadius: 'inherit',
        overflow: 'hidden',
      }}
    >
      {/* Dark background */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showDarkBg ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.9)',
          zIndex: 0,
        }}
      />

      {/* Pixie dust effect */}
      <AnimatePresence>
        {showEffect && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ position: 'absolute', inset: 0, zIndex: 1 }}
          >
            <ProfileEffectOverlay
              effect={PIXIE_DUST_EFFECT}
              fillContainer
              zIndex={1}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* === CARD PHASE: Fixed container with pulsing card bg === */}
      <AnimatePresence>
        {phase === 'card' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.3 } }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            {/* Fixed size container - nothing inside moves */}
            <Box sx={{
              position: 'relative',
              width: 300,
              height: 340,
            }}>
              {/* Card background - only this pulses */}
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  transformOrigin: 'center bottom',
                  zIndex: 1,
                }}
              >
                <Box sx={{
                  bgcolor: isDark ? 'background.default' : '#fff',
                  borderRadius: '24px',
                  height: 260,
                  boxShadow: `0 12px 28px ${accentColor}66`,
                  border: `4px solid ${accentColor}`,
                }} />
              </motion.div>

              {/* Chest - fixed position, doesn't pulse */}
              <Box sx={{
                position: 'absolute',
                top: -80,
                left: -150,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 2,
              }}>
                <Box sx={{ width: 300 }}>
                  <Lottie
                    animationData={treasureChestAnimation}
                    loop={false}
                    autoplay={false}
                    initialSegment={[0, 1]}
                    style={{ width: '450px', height: 'auto' }}
                  />
                </Box>
              </Box>

              {/* Text - fixed position, doesn't pulse */}
              <Box sx={{
                position: 'absolute',
                top: 250,
                bottom: 16,
                left: 0,
                right: 0,
                textAlign: 'center',
                zIndex: 3,
              }}>
                <Typography
                  variant="h6"
                  fontWeight={900}
                  sx={{ color: textColor, mb: 0.5 }}
                >
                  {isBonus ? 'Viikonloppubonus!' : 'Viikkoraha!'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', display: 'block' }}
                >
                  {isBonus ? 'Ansaitsit lisää viikonloppuna! 👆' : 'Napauta avataksesi 👆'}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* === ANIMATED CHEST PHASES === */}
      <AnimatePresence>
        {showAnimatedChest && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            {/* Fixed size container - same as card phase */}
            <Box sx={{
              position: 'relative',
              width: 300,
              height: 340,
            }}>
              {/* Coin explosion - centered on chest */}
              {showCoinExplosion && (
                <Box
                  key="coin-explosion"
                  sx={{
                    position: 'absolute',
                    top: -100,
                    left: -80,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 25,
                    pointerEvents: 'none',
                  }}
                >
                  <Particles
                    type="coins"
                    count={40}
                    duration={2000}
                    spread={120}
                    gravity={0.07}
                    direction="up"
                  />
                </Box>
              )}

              {/* Coin counter - above chest */}
              <AnimatePresence>
                {showCounter && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5, y: 40 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 150, damping: 12 }}
                    style={{
                      position: 'absolute',
                      top: -10,
                      left: 0,
                      right: 0,
                      textAlign: 'center',
                      zIndex: 15,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '3.5rem',
                        fontWeight: 700,
                        color: '#ffd700',
                        textShadow: '0 4px 24px rgba(255,215,0,0.7), 0 2px 8px rgba(0,0,0,0.5)',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      +{displayCoins} 🪙
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Animated Lottie chest - same position as static */}
              <Box sx={{
                position: 'absolute',
                top: -80,
                left: -150,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                zIndex: 10,
              }}>
                <Box sx={{ width: 300 }}>
                  <Lottie
                    lottieRef={lottieRef}
                    animationData={treasureChestAnimation}
                    loop={phase === 'shaking' || phase === 'idle'}
                    autoplay={false}
                    style={{ width: '450px', height: 'auto' }}
                  />
                </Box>
              </Box>

              {/* Result card - below chest */}
              <AnimatePresence>
                {showResult && (
                  <motion.div
                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      zIndex: 15,
                    }}
                  >
                    <Box sx={{
                      bgcolor: 'rgba(255,255,255,0.98)',
                      borderRadius: '20px',
                      p: 2,
                      px: 4,
                      textAlign: 'center',
                      boxShadow: '0 8px 32px rgba(255,215,0,0.5)',
                      border: '3px solid #ffd700',
                    }}>
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        sx={{ color: '#333' }}
                      >
                        Yhteensä: {oldBalance + coinsAwarded} 🪙
                      </Typography>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tap to dismiss hint */}
      {phase === 'idle' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{
            position: 'absolute',
            bottom: '8%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 15,
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
            Napauta jatkaaksesi
          </Typography>
        </motion.div>
      )}
    </Box>
  );
}
